// @ts-nocheck
// "Aproveite também" — lista compacta e unida (só APRESENTAÇÃO). Os dados (resolvedBumps), a seleção (selectedBumps) e os totais continuam
// sendo os da BookingPage: este componente só desenha e chama onToggle/isSelected, que são a mesma lógica de antes.
import React, { useId, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Add, Check } from '@mui/icons-material';

const money = (v: number) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;

// Contraste: a cor da empresa (configurável) é clareada (fundo escuro) ou escurecida (fundo claro) SÓ até atingir o mínimo; quem já passa não muda.
const toRgb = (hex: string) => { const h = (hex || '').replace('#', ''); return h.length >= 6 ? [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) : null; };
const lumOf = (c: number[]) => { const f = (v: number) => { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); }; return 0.2126 * f(c[0]) + 0.7152 * f(c[1]) + 0.0722 * f(c[2]); };
const ratioOf = (a: number[], b: number[]) => { const x = lumOf(a), y = lumOf(b); return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05); };
const ensure = (hex: string, surfaceHex: string, alvoRatio: number) => {
  const c = toRgb(hex), bg = toRgb(surfaceHex);
  if (!c || !bg) return hex;
  const tint = bg.map((v, i) => Math.round(c[i] * 0.1 + v * 0.9)); // fundo da linha selecionada (destaque a 10% sobre a superfície)
  const alvo = lumOf(bg) < 0.5 ? 255 : 0;
  const misturar = (t: number) => c.map((v) => Math.round(v + (alvo - v) * t));
  let t = 0, out = c;
  while (Math.min(ratioOf(out, bg), ratioOf(out, tint)) < alvoRatio && t < 0.85) { t += 0.04; out = misturar(t); }
  return '#' + out.map((v) => v.toString(16).padStart(2, '0')).join('');
};
const onAccent = (hex: string) => {
  const c = toRgb(hex); if (!c) return '#fff';
  return (0.299 * c[0] + 0.587 * c[1] + 0.114 * c[2]) / 255 > 0.62 ? '#111827' : '#ffffff';
};

interface Props {
  bumps: any[];                       // resolvedBumps da BookingPage (mesma ordem, mesmo limite)
  isSelected: (b: any) => boolean;    // mesma regra de seleção de antes
  onToggle: (b: any) => void;         // mesmo setSelectedBumps de antes
  accentColor: string;
  T: any;
}

export default function AddonsList({ bumps, isSelected, onToggle, accentColor, T }: Props) {
  const uid = useId().replace(/:/g, '');
  const [aberto, setAberto] = useState<string | null>(null);
  const [semFoto, setSemFoto] = useState<Record<string, boolean>>({});

  const textP = T?.textPrimary ?? '#202124';
  const textS = T?.textSecondary ?? '#586174';
  const line = T?.border ?? '#e0e4eb';
  const surface = T?.surfaceAlt ?? '#f9fafb';
  const surfaceHex = T?.surfaceAlt ?? '#f9fafb';
  const controlBorder = T ? '#9aa6b5' : '#728095';
  const savingColor = T ? '#9fe4ba' : '#176b3c';
  const accentUI = ensure(accentColor, surfaceHex, 3.1);     // barra, controle selecionado
  const accentText = ensure(accentColor, surfaceHex, 4.6);   // preço (texto)
  const accentInk = onAccent(accentUI);
  const selectedBg = `${accentColor}1a`;

  const itens = bumps.map((b: any) => {
    const key = b._bumpType === 'manual' ? `manual-${b.name}` : `${b._bumpType}-${b.id}`;
    const preco = Number(b.price || 0);
    const original = Number(b.original_price || 0);
    const promo = original > preco; // mesma regra de antes: só é promoção se o preço original cadastrado for maior que o atual
    const paragrafos = Array.from(new Set([b.description, b.details].filter((x: any) => typeof x === 'string' && x.trim()).flatMap((x: string) => x.split(/\n+/)).map((x: string) => x.trim()).filter(Boolean)));
    return { b, key, preco, original, promo, economia: promo ? original - preco : 0, img: b.image_url_md || b.image_url || '', paragrafos, sel: !!isSelected(b) };
  });

  const marcados = itens.filter((i) => i.sel);
  const extra = marcados.reduce((t, i) => t + i.preco, 0);
  const economiaTotal = marcados.reduce((t, i) => t + i.economia, 0);
  const resumo = marcados.length
    ? `${marcados.length} ${marcados.length === 1 ? 'adicional' : 'adicionais'} · ${money(extra)}${economiaTotal > 0 ? ` · Economia de ${money(economiaTotal)}` : ''}`
    : 'Nenhum adicional selecionado';

  const focoBotao = { '&:focus-visible': { outline: `2px solid ${textP}`, outlineOffset: 2 } };

  return (
    <Box component="section" aria-labelledby={`${uid}-t`} data-testid="addons-v2">
      <Typography component="h3" id={`${uid}-t`} sx={{ fontSize: 15, fontWeight: 700, color: textP, mb: 0.4 }}>Aproveite também</Typography>
      <Typography sx={{ fontSize: 13, lineHeight: 1.5, color: textS, mb: 1.4 }}>Adicione somente o que você quiser.</Typography>
      <Box component="ul" aria-label="Adicionais opcionais" sx={{ m: 0, p: 0, listStyle: 'none', border: `1px solid ${line}`, borderRadius: '8px', overflow: 'hidden', bgcolor: surface }}>
        {itens.map((it, i) => {
          const { b, key, sel } = it;
          const temFoto = !!it.img && !semFoto[key];
          const temDetalhe = it.paragrafos.length > 0;
          const idDet = `${uid}-d${i}`;
          const expandido = aberto === key;
          const alternar = () => setAberto(expandido ? null : key);
          const rotuloPreco = it.promo ? `${money(it.preco)}, antes ${money(it.original)}` : money(it.preco);
          const info = (
            <>
              <Typography component="strong" sx={{ fontSize: 14, lineHeight: 1.35, fontWeight: 600, color: textP, overflowWrap: 'anywhere' }}>{b.name}</Typography>
              {b.description ? (
                <Typography component="span" sx={{ display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 1, overflow: 'hidden', fontSize: 12, lineHeight: 1.5, color: textS, mt: '3px', maxWidth: '100%', overflowWrap: 'anywhere' }}>{b.description}</Typography>
              ) : null}
              <Box component="span" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 1, rowGap: '3px', mt: '5px', maxWidth: '100%' }}>
                {it.promo ? (
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: '7px', whiteSpace: 'nowrap' }}>
                    <Box component="s" aria-hidden="true" sx={{ fontSize: 12, lineHeight: 1.45, color: textS, textDecorationThickness: '1px', fontVariantNumeric: 'tabular-nums' }}>{money(it.original)}</Box>
                    <Box component="strong" aria-hidden="true" sx={{ fontSize: 14, lineHeight: 1.45, fontWeight: 600, color: accentText, fontVariantNumeric: 'tabular-nums' }}>{money(it.preco)}</Box>
                  </Box>
                ) : (
                  <Box component="strong" sx={{ fontSize: 14, lineHeight: 1.45, fontWeight: 600, color: accentText, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{money(it.preco)}</Box>
                )}
                {!it.promo && temDetalhe && <Box component="span" sx={{ fontSize: 11, lineHeight: 1.5, color: textS, textDecoration: 'underline', textUnderlineOffset: '3px', whiteSpace: 'nowrap' }}>{expandido ? 'Ver menos' : 'Ver mais'}</Box>}
              </Box>
              {it.promo && (
                <Box component="span" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 1, rowGap: '3px', mt: '2px', maxWidth: '100%' }}>
                  <Box component="span" sx={{ fontSize: 11, lineHeight: 1.5, fontWeight: 600, color: savingColor, whiteSpace: 'nowrap' }}>Economize {money(it.economia)}</Box>
                  {temDetalhe && <Box component="span" sx={{ fontSize: 11, lineHeight: 1.5, color: textS, textDecoration: 'underline', textUnderlineOffset: '3px', whiteSpace: 'nowrap' }}>{expandido ? 'Ver menos' : 'Ver mais'}</Box>}
                </Box>
              )}
            </>
          );
          const infoSx = { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0, minHeight: 60, py: '2px', px: 0, m: 0, border: 0, borderRadius: '2px', bgcolor: 'transparent', color: textP, textAlign: 'left', font: 'inherit' };
          return (
            <Box component="li" key={key} data-selected={sel ? 'true' : 'false'} sx={{ borderTop: i ? `1px solid ${line}` : 'none', bgcolor: sel ? selectedBg : 'transparent', boxShadow: sel ? `inset 3px 0 0 ${accentUI}` : 'none', transition: 'background-color .12s' }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: temFoto ? '52px minmax(0,1fr) 44px' : 'minmax(0,1fr) 44px', alignItems: 'center', gap: 1, py: '12px', pl: temFoto ? '10px' : '12px', pr: '6px', minHeight: temFoto ? 96 : 80 }}>
                {temFoto && (temDetalhe ? (
                  <Box component="button" type="button" aria-label={`Ver detalhes de ${b.name}`} aria-expanded={expandido} aria-controls={idDet} onClick={alternar}
                    sx={{ position: 'relative', display: 'block', width: 52, height: 52, p: 0, border: `1px solid ${line}`, borderRadius: '8px', bgcolor: '#fff', overflow: 'hidden', cursor: 'pointer', ...focoBotao }}>
                    <img src={it.img} alt="" loading="lazy" onError={() => setSemFoto((s) => ({ ...s, [key]: true }))} style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Box>
                ) : (
                  <Box sx={{ position: 'relative', width: 52, height: 52, border: `1px solid ${line}`, borderRadius: '8px', bgcolor: '#fff', overflow: 'hidden' }}>
                    <img src={it.img} alt="" loading="lazy" onError={() => setSemFoto((s) => ({ ...s, [key]: true }))} style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }} />
                  </Box>
                ))}
                {temDetalhe ? (
                  <Box component="button" type="button" aria-expanded={expandido} aria-controls={idDet} onClick={alternar} sx={{ ...infoSx, cursor: 'pointer', ...focoBotao }}>{info}</Box>
                ) : (
                  <Box sx={infoSx}>{info}</Box>
                )}
                <Box component="label" sx={{ position: 'relative', display: 'grid', placeItems: 'center', width: 44, height: 44, cursor: 'pointer', '& input:focus-visible + .ad-sq': { outline: `3px solid ${textP}`, outlineOffset: 3 } }}>
                  <input type="checkbox" checked={sel} onChange={() => onToggle(b)} aria-label={`Adicionar ${b.name} por ${rotuloPreco}`}
                    style={{ position: 'absolute', inset: 0, width: 44, height: 44, opacity: 0, margin: 0, cursor: 'pointer' }} />
                  <Box className="ad-sq" aria-hidden="true" sx={{ display: 'grid', placeItems: 'center', width: 24, height: 24, border: `1.5px solid ${sel ? accentUI : controlBorder}`, borderRadius: '6px', bgcolor: sel ? accentUI : 'transparent', color: sel ? accentInk : textS, transition: 'background-color .12s, border-color .12s' }}>
                    {sel ? <Check sx={{ fontSize: 16 }} /> : <Add sx={{ fontSize: 16 }} />}
                  </Box>
                </Box>
              </Box>
              {temDetalhe && (
                <Box id={idDet} hidden={!expandido} sx={{ px: '14px', pb: '14px' }}>
                  {it.paragrafos.map((p: string, k: number) => (
                    <Typography key={k} sx={{ fontSize: 13, lineHeight: 1.6, color: textS, mt: k ? 1 : 0, pt: k === 0 ? '12px' : 0, borderTop: k === 0 ? `1px solid ${line}` : 'none', overflowWrap: 'anywhere' }}>{p}</Typography>
                  ))}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
      <Typography aria-live="polite" data-testid="addons-resumo" sx={{ fontSize: 12, lineHeight: 1.5, color: textS, mt: 1.1, minHeight: 18 }}>{resumo}</Typography>
    </Box>
  );
}
